import { useEffect, useState } from "react";
import { Dialog, DialogPanel } from "@headlessui/react";
import { IoMdClose } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { commonToast } from "../../helper/Toast";
import Loading from "../Loading";
import {
  getApiError,
  getTeacher,
  getTeacherId,
  parseItemResponse,
  updateTeacher,
} from "../../helper/adminUsersApi";

const LANGUAGE_OPTIONS = [
  { value: "en_GB", label: "en_GB" },
  { value: "lt_LT", label: "lt_LT" },
  { value: "es_ES", label: "es_ES" },
];

const COUNTRY_OPTIONS = [
  { value: "LT", label: "LT" },
  { value: "GB", label: "GB" },
  { value: "ES", label: "ES" },
];

function normalizeLanguage(value) {
  const code = String(value || "").trim();
  if (LANGUAGE_OPTIONS.some((option) => option.value === code)) return code;
  const lower = code.toLowerCase();
  if (lower.startsWith("lt")) return "lt_LT";
  if (lower.startsWith("es")) return "es_ES";
  return "en_GB";
}

function subjectsToText(subjects) {
  if (Array.isArray(subjects)) return subjects.filter(Boolean).join(", ");
  return subjects || "";
}

function toFormValues(teacher) {
  return {
    firstName: teacher?.firstName || "",
    lastName: teacher?.lastName || "",
    email: teacher?.email || teacher?.username || "",
    preferredLanguage: normalizeLanguage(teacher?.preferredLanguage),
    countryCode: teacher?.countryCode || "LT",
    subjectsToTeach: subjectsToText(teacher?.subjectsToTeach),
  };
}

export default function EditTeacherModel({ open, setOpen, teacher, schoolId, onUpdated }) {
  const { t } = useTranslation();
  const [initialValues, setInitialValues] = useState(toFormValues(teacher));

  useEffect(() => {
    if (!open) return undefined;
    setInitialValues(toFormValues(teacher));

    const teacherId = getTeacherId(teacher);
    if (!teacherId) return undefined;

    let cancelled = false;
    getTeacher(teacherId)
      .then((response) => {
        const item = parseItemResponse(response);
        if (!cancelled && item) setInitialValues(toFormValues(item));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, teacher]);

  const validationSchema = Yup.object({
    firstName: Yup.string().required(t("validation.name_required")),
    lastName: Yup.string().required(t("validation.lastname_required")),
    email: Yup.string()
      .email(t("validation.email_invalid"))
      .required(t("validation.email_required")),
    countryCode: Yup.string().required(t("country_code")),
  });

  const handleClose = () => {
    requestAnimationFrame(() => setOpen(false));
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const teacherId = getTeacherId(teacher);
    const username = teacher?.username || teacher?.email || values.email.trim();
    if (!teacherId && !username) {
      commonToast(t("teacher_management.teacher_not_found"), "error");
      return;
    }

    try {
      const schoolIds = Array.isArray(teacher?.schoolIds) && teacher.schoolIds.length
        ? teacher.schoolIds
        : schoolId
          ? [schoolId]
          : [];
      const subjectsToTeach = values.subjectsToTeach
        .split(",")
        .map((subject) => subject.trim())
        .filter(Boolean);

      const payload = {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        preferredLanguage: values.preferredLanguage,
        countryCode: values.countryCode,
        schoolIds,
        subjectsToTeach,
      };
      if (teacherId) payload.teacherId = teacherId;
      if (username) payload.username = username;

      await updateTeacher(payload);
      commonToast(t("teacher_management.edit_success"), "success");
      onUpdated?.();
      handleClose();
    } catch (error) {
      commonToast(getApiError(error, t("teacher_management.edit_failed")), "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      as="div"
      className="relative z-40 focus:outline-none"
      onClose={handleClose}
    >
      <div className="fixed inset-0 bg-black/40" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md rounded-2xl bg-white p-6">
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting }) => (
                <Form>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900">
                      {t("teacher_management.edit_teacher")}
                    </h3>
                    <IoMdClose
                      className="cursor-pointer text-gray-500 hover:text-gray-800"
                      fontSize={24}
                      onClick={handleClose}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-y-3">
                    <InputField label={t("first_name") || t("name")} name="firstName" />
                    <InputField label={t("last_name") || t("lastname")} name="lastName" />
                    <InputField label={t("email")} name="email" type="email" />
                    <SelectField
                      label={t("preferred_language")}
                      name="preferredLanguage"
                      options={LANGUAGE_OPTIONS}
                    />
                    <SelectField
                      label={t("country_code")}
                      name="countryCode"
                      options={COUNTRY_OPTIONS}
                    />
                    <InputField
                      label={t("teacher_management.subjects")}
                      name="subjectsToTeach"
                      placeholder="Mathematics, Physics"
                    />
                  </div>

                  <div className="mt-6 flex justify-end gap-4">
                    <button type="button" className="secondary-btn" onClick={handleClose}>
                      {t("cancel")}
                    </button>
                    <button type="submit" className="primary-btn" disabled={isSubmitting}>
                      {isSubmitting ? <Loading size={5} /> : t("save") || "Save"}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

const InputField = ({ label, name, type = "text", placeholder }) => (
  <div>
    <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    <Field
      type={type}
      name={name}
      placeholder={placeholder}
      autoComplete="off"
      className="border h-[45px] w-full rounded-lg px-3 shadow-inner outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
    />
    <ErrorMessage name={name} component="div" className="text-red-500 text-xs mt-1" />
  </div>
);

const SelectField = ({ label, name, options }) => (
  <div>
    <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    <Field
      as="select"
      name={name}
      className="border h-[45px] w-full rounded-lg px-3 shadow-inner outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 bg-white"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </Field>
    <ErrorMessage name={name} component="div" className="text-red-500 text-xs mt-1" />
  </div>
);
